import { Box, Paper, styled, useTheme } from "@mui/material";
import { GridCellEditStopParams, gridClasses, GridColDef } from "@mui/x-data-grid";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FormDefinitions, LotProperty, LotPropertyBulkEditRow, Property } from "../api/dtos";
import SetLinks from "../components/layout/SetLinks";
import LotPropertyBulkAssignPanel from "../components/LotPropertyBulkAssignPanel";
import { StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import { LotPropertyProjectCreateLink, LotPropertyStageCreateLink } from "../contexts/layouts/LinkContext";
import PropertySelectForm from "../forms/PropertySelectForm";
import useLotPropertiesByProjectIdStageIdPropertyIds from "../hooks/data/useLotPropertiesByProjectIdStageIdPropertyIds";
import usePostLotPropertyEdit from "../hooks/data/usePostLotPropertyEdit";
import useProperties from "../hooks/data/useProperties";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const useLotPropertyColumns = (properties : Property[]) => {

    const metadata = useFormMetadata(FormDefinitions.LotProperty);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const lotPropertyColumns = useMemo(() => {
        const columns: GridColDef<LotPropertyBulkEditRow>[] =  [
            {
                field: "lotId",
                headerName: "Lot Number",
                renderCell: (params) => GetFieldLookupItem(lookups, 'LotId', params.row.lotId)?.value?.toString(),
                flex: 1
            }
        ];
        
        properties.forEach((property, index) => columns.push(
            {
                field: `properties[${index}]`,
                valueGetter: (_value, row) => row.properties?.filter(cell => cell.propertyId === property.id)[0]?.value ?? undefined, 
                valueSetter:(value, row)=>{
                    const rowProperty = row.properties?.filter(lotProperty => lotProperty.propertyId == property.id)[0];

                    const rowPropertyToUse = rowProperty ?? new LotProperty({lotId: row.lotId, propertyId: property.id});
                    rowPropertyToUse.value = value;

                    if(!rowProperty) {
                        row.properties?.push(rowPropertyToUse);
                    }

                    return row;
                },
                headerName: property.name,
                editable: true,
                flex: 1
            }
        ))

        return columns;
    }, [lookups, properties]);

    return lotPropertyColumns;
}

export const BulkEditLotProperties: FC = () => {
    const params = useParams();

    const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);
    const [formOpen, setFormOpen] = useState<boolean>(false);

    const projectId = Number(params.projectId);
    const stageId = Number(params.stageId);

    const lotPropertyMetadata= useFormMetadata(FormDefinitions.LotProperty);

    const theme = useTheme();

    const submit = usePostLotPropertyEdit();
    
    const {data: properties} = useProperties();

    const {data: _lotPropertyLookups, loading: _lookupsLoading} = useLookups(lotPropertyMetadata);
    
    const {data: lotProperties, loading: lotPropertiesLoading, refresh: _lotPropertiesRefresh} = useLotPropertiesByProjectIdStageIdPropertyIds(
        projectId, 
        stageId, 
        selectedProperties.filter(value => 
            value.id != undefined
        ).map(value => value.id!)
    );  
    
    const columns = useLotPropertyColumns(selectedProperties ?? []);

    const cellEditSubmit = async (params: GridCellEditStopParams<any, any, any>) => {

        const regex = /.*?\[(?<Index>[0-9]+)\]$/;

        const res = params.colDef.field.match(regex);

        const index = res?.groups?.['Index'] as unknown as number;

        //const lotId = params.row.lotId;
        const propertyId = selectedProperties[index].id;
        const lotId = params.row.lotId;

        await submit(lotId, propertyId, params.value);
    }

    return (
        <>
            <SetLinks 
                createProjectLink={LotPropertyProjectCreateLink} 
                createStageLink={LotPropertyStageCreateLink} 
                lotLinkEnabled={false} 
            />

            <PropertySelectForm 
                updateProperties={setSelectedProperties} 
                initSelection={selectedProperties} 
                onClose={() => setFormOpen(false)} 
                open={formOpen}
            />
            
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <StyledButton 
                    sx={{
                        marginBottom: theme.spacing(2)
                    }}
                    onClick={() => setFormOpen(true)}
                >
                    Select Properties
                </StyledButton>
            </Box>

            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        editMode="cell"
                        //onRowEditStop={(params, event) => lotEditSubmit(params, event)}
                        onCellEditStop ={async (params) => await cellEditSubmit(params)}
                        startCollapsed={true} 
                        getRowId={(row) => row.lotId ?? 0}
                        title={"Lots"} 
                        loading={lotPropertiesLoading} 
                        columns={columns}
                        rows={lotProperties}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        // {...CustomToolbarProps}
                    />
                </Box>
            </StyledPaper>
            <LotPropertyBulkAssignPanel 
                projectId={projectId}
                stageId={stageId}
                properties={properties ?? []} 
            />
        </>
    );
}